import React, { Component } from "react";
import classNames from "classnames";
import moment from "moment";
import { bool, func, instanceOf, objectOf, shape, string } from "prop-types";

import FieldError from "pages/_components/fields/FieldError";
import FieldLabel from "pages/_components/fields/FieldLabel";
import DatePicker from "pages/_components/fields/datepicker";

import * as i18n from "util/i18n";
import { validateToday } from "util/date";

class DateField extends Component {
    static propTypes = {
        field: shape({ name: string }).isRequired,
        form: shape({ errors: objectOf(string), touched: objectOf(bool) }).isRequired,
        handleChange: func,
        hideLabel: bool,
        labelKey: string,
        labelText: string,
        hidePlaceholder: bool,
        placeholder: string,
        endDate: instanceOf(moment),
        maxDate: instanceOf(moment),
        minDate: instanceOf(moment),
        dateFormat: string,
        idForm: string,
        showDisabledMonthNavigation: bool,
        startDate: instanceOf(moment),
        showYearDropdown: bool,
        showMonthDropdown: bool,
        idField: string.isRequired,
        resetFilters: bool,
        handleResetDateFilters: func,
        withTodayValidation: bool,
        noEdit: bool,
        allowNullValue: bool,
        popperPlacement: string,
        disabled: bool,
    };

    static defaultProps = {
        handleChange: () => {},
        hideLabel: false,
        labelKey: null,
        labelText: null,
        hidePlaceholder: false,
        placeholder: "",
        maxDate: moment(),
        minDate: moment().add(-12, "months"),
        dateFormat: null,
        endDate: null,
        idForm: "",
        showDisabledMonthNavigation: true,
        startDate: null,
        showYearDropdown: true,
        showMonthDropdown: true,
        resetFilters: false,
        handleResetDateFilters: null,
        withTodayValidation: false,
        noEdit: false,
        allowNullValue: true,
        popperPlacement: "bottom",
        disabled: false,
    };

    state = {
        isFocused: false,
        selectedDate: null,
    };

    validateDate = (selectedDate) => {
        const { maxDate, minDate } = this.props;

        if (selectedDate && minDate && selectedDate.diff(moment(minDate)) < 0) {
            return false;
        }

        if (selectedDate && maxDate && selectedDate.diff(moment(maxDate) >= 0)) {
            return false;
        }

        return true;
    };

    handleChange = (_selectedDate) => {
        const { field, form, handleChange, withTodayValidation, allowNullValue } = this.props;
        const selectedDate = withTodayValidation ? validateToday(_selectedDate) : _selectedDate;

        if (!this.validateDate(selectedDate) || (!allowNullValue && !selectedDate)) {
            return;
        }

        if (handleChange) {
            handleChange(selectedDate);
        }

        this.setState({ selectedDate });

        form.setFieldValue(field.name, selectedDate ? selectedDate.toDate() : null);
    };

    onChangeRaw = (e) => {
        const { noEdit } = this.props;
        if (noEdit) {
            e.preventDefault();
        }
    };

    render() {
        const { isFocused, selectedDate } = this.state;
        const {
            endDate,
            field,
            form: { touched, errors, setFieldValue },
            hideLabel,
            labelKey,
            labelText,
            hidePlaceholder,
            placeholder,
            idForm,
            maxDate,
            minDate,
            showDisabledMonthNavigation,
            startDate,
            dateFormat,
            idField,
            resetFilters,
            handleResetDateFilters,
            noEdit,
            popperPlacement,
            disabled,
            ...rest
        } = this.props;

        if (resetFilters) {
            if (handleResetDateFilters) {
                handleResetDateFilters(false);
            }
            this.setState({ selectedDate: null });
            setFieldValue(field.name, null);
        }
        const hasError = touched && touched[field.name] && errors && errors[field.name];
        return (
            <div
                className={classNames("form-group", "form-group--datepicker", {
                    "has-error": hasError,
                    "has-focus": isFocused,
                })}>
                {!hideLabel && (
                    <FieldLabel
                        labelKey={labelKey || `${idForm}.${field.name}.label`}
                        labelText={labelText}
                        idField={field.name}
                    />
                )}
                <div
                    className={`input-group-standard ${disabled ? " is-disabled" : ""} 
                    } `}>
                    <div className="form-control">
                        <DatePicker
                            autoComplete="off"
                            dateFormat={dateFormat === null ? i18n.get("datepicker.format") : dateFormat}
                            className="form-control"
                            endDate={endDate ? moment(endDate) : undefined}
                            maxDate={maxDate ? moment(maxDate) : undefined}
                            minDate={minDate ? moment(minDate) : undefined}
                            placeholderText={
                                hidePlaceholder ? "" : i18n.get(`${idForm}.${field.name}.placeholder`, placeholder)
                            }
                            selected={selectedDate || (field.value && moment(field.value))}
                            showDisabledMonthNavigation={showDisabledMonthNavigation}
                            startDate={startDate ? moment(startDate) : undefined}
                            onChange={this.handleChange}
                            id={field.name}
                            onChangeRaw={noEdit && this.onChangeRaw}
                            popperPlacement={popperPlacement}
                            popperModifiers={{
                                flip: {
                                    behavior: ["bottom"], // don't allow it to flip to be above
                                },
                                preventOverflow: {
                                    enabled: false, // tell it not to try to stay within the view (this prevents the popper from covering the element you clicked)
                                },
                                hide: {
                                    enabled: false, // turn off since needs preventOverflow to be enabled
                                },
                            }}
                            disabled={disabled}
                            {...rest}
                        />
                    </div>
                </div>
                {hasError && <FieldError error={errors[field.name]} />}
            </div>
        );
    }
}

export default DateField;
